import { useDispatch, useSelector } from 'react-redux';

import store from 'app/store';
import {
  FormsConfigActions,
  selectIsMubiLeasingConfig,
} from 'app/store/data/formsConfig';
import { selectShouldCheckIfConsentResetRequired } from 'app/store/data/consents';
import { getQuotationId } from 'app/store/data/quotation';
import { checkAndResetMarketingConsentIfRequired } from 'app/shared/helpers/checkAndResetMarketingConsent';
import { WrappedControlProps } from 'app/components/Controls/WrappedControlProps';
import { isValidPhoneNumber } from 'app/validators/validatePhoneNumber';
import { clearAnonymization } from 'app/shared/helpers/clearAnonymization';
import { isAutofillChange } from 'app/shared/helpers/isAutofillChange';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import { getMessageKeyWithSuffix } from 'app/services/validation.service';
import {
  shouldValidatePhoneNumberWithBackend,
  validatePhoneNumberWithBackend,
} from 'app/services/phoneNumbers/validatePhoneNumberWithBackend';

export function usePhoneNumberControl({
  input,
  input: { value },
  meta: { touched, valid },
  questionKey,
  onClick,
  onControlBlur,
  onValueChange,
  formName = FormsEnum.CalculationForm,
}: WrappedControlProps) {
  const dispatch = useDispatch();
  const isMubiLeasingConfig = useSelector(selectIsMubiLeasingConfig);
  const shouldCheckIfConsentResetRequired = useSelector(
    selectShouldCheckIfConsentResetRequired
  );
  const quotationId = useSelector(getQuotationId);

  const prefix = '+48';
  const error = touched && !valid;

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) =>
    handleFocus(e.target.value);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventValue = event.target.value
      .replace(prefix, '')
      .replace(/[^\d]/gi, '')
      .trim()
      .slice(0, 9);

    input.onChange(eventValue);

    if (
      [FormsEnum.CheckoutForm, FormsEnum.TravelCheckoutForm].includes(formName)
    ) {
      dispatch(
        FormsConfigActions.setMessageVisibilityAction({
          formName,
          key: getMessageKeyWithSuffix(
            questionKey,
            'backend_invalid_phone_number'
          ),
          visible: false,
        })
      );
    }

    if (onValueChange) {
      onValueChange(eventValue, false, false);
    }

    if (isAutofillChange(event.target)) {
      handleBlur(eventValue);
    }
  };

  const handleFocus = (value: string) => {
    const clearedValue = clearAnonymization(value);

    if (clearedValue !== value) {
      input.onChange(clearedValue);
      if (onValueChange) {
        onValueChange(clearedValue, false, false);
      }
    }
  };

  const handleBlur = async (autofilledValue?: string) => {
    const isAutofilled = autofilledValue !== undefined;
    const currentValue =
      autofilledValue === undefined ? value : autofilledValue;

    input.onBlur(currentValue);

    const isValid = isValidPhoneNumber(currentValue);

    if (
      shouldValidatePhoneNumberWithBackend(formName, questionKey as FieldsEnum)
    ) {
      await validatePhoneNumberWithBackend(
        dispatch,
        store.getState,
        formName,
        questionKey,
        currentValue
      );
    }

    if (isValid && shouldCheckIfConsentResetRequired.phoneNumber) {
      await checkAndResetMarketingConsentIfRequired({
        quotationId,
        dispatch,
        shouldCheck: shouldCheckIfConsentResetRequired.phoneNumber,
        requestData: {
          phoneNumber: currentValue,
        },
        fieldToResetPath: {
          type: 'root',
          field: 'phoneNumber',
        },
        formName,
        consentFieldKey:
          formName === FormsEnum.CheckoutForm
            ? FieldsEnum.InsuranceMarketingConsent
            : FieldsEnum.InsuranceMarketingEmailConsent,
      });
    }

    const shouldPatch = isValid || currentValue === '';

    if (onControlBlur) {
      onControlBlur(
        shouldPatch,
        true,
        false,
        isAutofilled,
        isAutofilled ? currentValue : undefined
      );
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return {
    error,
    handleBlur,
    handleChange,
    onFocus,
    handleClick,
    prefix,
    isMubiLeasingConfig,
    shouldCheckIfConsentResetRequired,
  };
}
